/**
 * Keeps React alive on a page an in-browser translator has rewritten.
 *
 * Google Translate (Chrome's own "translate this page", and every extension built on it)
 * moves each text node into a `<font>` wrapper it owns. React keeps rendering against the
 * tree it built, so the moment it inserts a sibling before such a text node — a spinner
 * appearing inside a button, say — or removes one, the reference node is no longer a child
 * of the parent React hands the DOM, and `insertBefore`/`removeChild` throw `NotFoundError`.
 * Nothing catches that inside React's commit, so the whole route unmounts into the router's
 * error component: "Failed to execute 'insertBefore' on 'Node'".
 *
 * Dropping a mutation the DOM would have rejected anyway keeps the page usable. The tree
 * then differs from React's picture in exactly the spots the translator already owns, which
 * costs at most a node lingering a render longer.
 */
export function installTranslatedDomGuard(): void {
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode != null && referenceNode.parentNode !== this) {
      // The reference is gone from this parent: append instead, so the node still lands
      // in the tree and React's next render finds it where it expects.
      return originalInsertBefore.call(this, newNode, null) as T
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      child.parentNode?.removeChild(child)
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }
}
